import { useState, useEffect } from "react";
import { UserList } from "./user-list";
import { Link } from "react-router-dom";
import { UserPlus } from "lucide-react";
import { UserInviteForm } from "./user-invite-form";
import MainLayout from "../../components/layout/MainLayout";
import {
  getSubscriptionActiveDataCount,
  getSubscriptionData,
} from "../../../Services/subscriptionService";
import { useSelector } from "react-redux";

export default function UserInvitePage() {
  const [users, setUsers] = useState([]);
  const [userCount, setUserCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  const userDetails = useSelector((state) => state?.auth?.loggedInUser);

  const customerId = userDetails?._id;

  async function getSubscriptionDataAndCount() {
    try {
      setLoading(true);

      const dataResponse = await getSubscriptionData(customerId);
      const userDataResponse = dataResponse?.data;
      const filterUserDataResponse = userDataResponse?.filter(
        (item) => !item?.isDeleted && item?.isInvitedUser
      );

      const formattedUsers =
        filterUserDataResponse?.map((item, index) => ({
          id: item._id || index + 1,
          name: item.name || item.userName || "Unknown",
          email: item.userEmail || "",
          role: item.accessLevel || "User",
          status: item.isActive || "Pending",
          phoneNumber: item?.phoneNumber || "Not Available",
        })) || [];

      setUsers(formattedUsers);

      const countResponse = await getSubscriptionActiveDataCount(customerId);

      setUserCount(countResponse?.data || 0);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching subscription data:", error);
      setError("Failed to load user data. Please try again later.");
      setLoading(false);
    }
  }

  useEffect(() => {
    getSubscriptionDataAndCount();
  }, [customerId]);

  const handleInviteUser = (userData) => {
    const newUser = {
      id: users.length + 1,
      name: userData.name,
      email: userData.email,
      role: userData.accessLevel,
      status: "Pending",
      phoneNumber: userData?.phoneNumber,
    };
    setUsers([...users, newUser]);
    setShowModal(false);
  };

  const filteredUsers = users.filter((user) => {
    const matchesStatus =
      filterStatus === "all" ||
      user.status.toLowerCase() === filterStatus.toLowerCase();
    const matchesSearch =
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  return (
    <MainLayout title="User Invite Page">
      <div className="container mx-auto py-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex flex-row items-center justify-between pb-4">
            <div>
              <h1 className="text-2xl font-bold">Team Members</h1>
              <p className="text-gray-500">
                Manage your team and invite new members
              </p>
              {userCount > 0 && (
                <p className="text-sm text-gray-600">
                  Total active members: {userCount}
                </p>
              )}
            </div>

            <Link to={"/admin/users-access/user-invite"}>
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2">
                <UserPlus className="h-4 w-4" />
                Invite User
              </button>
            </Link>
          </div>

          <div className="flex items-center justify-between mb-4">
            <div className="relative">
              <button
                className="border rounded-md px-3 py-2 w-[180px] flex justify-between items-center text-sm"
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              >
                <span>
                  {filterStatus === "all"
                    ? "All Invitations"
                    : filterStatus.charAt(0).toUpperCase() +
                      filterStatus.slice(1)}
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>

              {showFilterDropdown && (
                <div className="absolute mt-1 w-[180px] bg-white border rounded-md shadow-lg z-10">
                  <div className="py-1">
                    {["All Invitations", "Pending", "Accepted", "Expired"].map(
                      (status) => (
                        <button
                          key={status}
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                          onClick={() => {
                            setFilterStatus(
                              status === "All Invitations"
                                ? "all"
                                : status.toLowerCase()
                            );
                            setShowFilterDropdown(false);
                          }}
                        >
                          {status}
                        </button>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="relative">
              <input
                className="border rounded-md pl-8 pr-3 py-2 w-[250px]"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="text-red-500 text-center py-4">{error}</div>
          ) : (
            <UserList
              users={filteredUsers}
              getSubscriptionDataAndCount={getSubscriptionDataAndCount}
            />
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="mb-4">
                <h2 className="text-xl font-semibold">Invite New User</h2>
                <p className="text-gray-500 text-sm">
                  Enter the details of the user you want to invite to your team.
                </p>
              </div>
              <UserInviteForm
                onSubmit={handleInviteUser}
                onCancel={() => setShowModal(false)}
              />
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
